
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, Repeat, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  revieweeId: string;
  revieweeName: string;
  onReviewSubmitted: () => void;
  isSubscriptionReview?: boolean;
  reviewType?: 'mentee_to_mentor' | 'mentor_to_mentee';
}

export const ReviewDialog = ({ 
  isOpen, 
  onClose, 
  bookingId, 
  revieweeId, 
  revieweeName,
  onReviewSubmitted,
  isSubscriptionReview = false,
  reviewType = 'mentee_to_mentor'
}: ReviewDialogProps) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Rating Required",
        description: "Please select a rating before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      console.log('DEBUG: Submitting review:', {
        bookingId,
        revieweeId,
        rating,
        isSubscriptionReview,
        reviewText: reviewText.trim(),
        tableName: isSubscriptionReview ? 'subscription_reviews' : 'appointment_reviews'
      });

      let error;

      if (isSubscriptionReview) {
        // For subscription reviews, use subscription_reviews table
        console.log('DEBUG: Inserting subscription review into subscription_reviews table');
        const { error: subscriptionError } = await supabase
          .from('subscription_reviews')
          .insert({
            application_id: bookingId, // This is the application_id for subscriptions
            reviewer_id: user.id,
            reviewee_id: revieweeId,
            rating,
            review_text: reviewText.trim() || null,
            review_type: reviewType
          });
        error = subscriptionError;
        
        if (subscriptionError) {
          console.error('DEBUG: Error inserting subscription review:', subscriptionError);
        } else {
          console.log('DEBUG: Successfully inserted subscription review into subscription_reviews');
        }
      } else {
        // For session reviews, use appointment_reviews table
        console.log('DEBUG: Inserting session review into appointment_reviews table');
        const { error: sessionError } = await supabase
          .from('appointment_reviews')
          .insert({
            booking_id: bookingId, // This is the session_booking_id for sessions
            reviewer_id: user.id,
            reviewee_id: revieweeId,
            rating,
            review_text: reviewText.trim() || null
          });
        error = sessionError;
        
        if (sessionError) {
          console.error('DEBUG: Error inserting session review:', sessionError);
        } else {
          console.log('DEBUG: Successfully inserted session review into appointment_reviews');
          
          // If this is a mentor submitting a session review, update mentor_review_submitted flag
          if (reviewType === 'mentor_to_mentee') {
            console.log('DEBUG: Updating mentor_review_submitted flag for booking:', bookingId);
            const { error: updateError } = await supabase
              .from('session_bookings')
              .update({ mentor_review_submitted: true })
              .eq('id', bookingId);
              
            if (updateError) {
              console.error('DEBUG: Error updating mentor_review_submitted flag:', updateError);
            } else {
              console.log('DEBUG: Successfully updated mentor_review_submitted flag');
            }
          }
        }
      }

      if (error) throw error;

      const experienceType = isSubscriptionReview ? 'subscription experience' : 'session';
      toast({
        title: "Review Submitted",
        description: `Thank you for your feedback about your ${experienceType} with ${revieweeName}!`,
      });

      console.log('DEBUG: Review submitted successfully, calling onReviewSubmitted');
      onReviewSubmitted();
      onClose();
      setRating(0);
      setReviewText('');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogTitle = () => {
    return isSubscriptionReview ? 'Review Subscription Experience' : 'Leave a Review';
  };

  const getQuestionText = () => {
    const experienceType = isSubscriptionReview ? 'subscription experience' : 'session';
    return `How was your ${experienceType} with ${revieweeName}?`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSubscriptionReview ? (
              <Repeat className="h-5 w-5 text-purple-600" />
            ) : (
              <Calendar className="h-5 w-5 text-blue-600" />
            )}
            {getDialogTitle()}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              {getQuestionText()}
            </p>
            
            {/* Star Rating */}
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <Star 
                    className={`h-6 w-6 ${
                      star <= rating 
                        ? 'fill-yellow-400 text-yellow-400' 
                        : 'text-gray-300'
                    }`} 
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Additional Comments (Optional)
            </label>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder={isSubscriptionReview 
                ? "Share your thoughts about the overall subscription experience..." 
                : "Share your experience..."
              }
              className="resize-none"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting}
              className="bg-emu-blue hover:bg-emu-blue/90"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
